import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const KPS_VAULT_NUMBERS = {
  WAVE_CI: '0749475280',
  ORANGE_CI: '0749475280',
  MTN_CI: '0556529897',
};

const computeSha256Sync = (rawInput) => {
  const utf8 = unescape(encodeURIComponent(String(rawInput || '')));
  const rightRotate = (value, amount) => (value >>> amount) | (value << (32 - amount));
  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let i = 0, j = 0, result = '';
  const words = [];
  const asciiBitLength = utf8.length * 8;
  let hash = [], k = [], primeCounter = 0, isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (i = 0; i < 313; i += candidate) isComposite[i] = candidate;
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }
  let str = utf8 + '\x80';
  while ((str.length % 64) - 56) str += '\x00';
  for (i = 0; i < str.length; i++) {
    j = str.charCodeAt(i);
    words[i >> 2] |= j << ((3 - (i % 4)) * 8);
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;
  for (j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16)), oldHash = hash.slice(0);
    for (i = 0; i < 64; i++) {
      const w15 = w[i - 15], w2 = w[i - 2], a = hash[0], e = hash[4];
      const temp1 = hash[7] + (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) + ((e & hash[5]) ^ (~e & hash[6])) + k[i] +
        (w[i] = i < 16 ? w[i] : (w[i - 16] + (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) + w[i - 7] + (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) | 0);
      const temp2 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) + ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }
    for (i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
  }
  for (i = 0; i < 8; i++) {
    for (j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result.toUpperCase();
};

const calculateKpsSplit = (grossAmount) => {
  const gross = Math.max(Math.round(Number(grossAmount) || 0), 0);
  const fee = gross < 5000 ? 0 : Math.min(Math.round(gross * 0.015), 25000);
  const net = Math.max(gross - fee, 0);
  return { gross, fee, net };
};

export default function KpsPayCheckout() {
  const searchParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);

  const initialRef = searchParams.get('ref') || `KPS-IN-${Date.now()}`;
  const initialAmount = parseInt(searchParams.get('amount') || '0', 10) || 0;
  const initialNetworkRaw = (searchParams.get('network') || 'WAVE_CI').toUpperCase();
  const initialNetwork = initialNetworkRaw === 'ORANGE_CI' || initialNetworkRaw === 'MTN_CI' ? initialNetworkRaw : 'WAVE_CI';
  const initialPhone = searchParams.get('phone') || '';
  const shouldAutoLaunch = searchParams.get('autolaunch') === '1';

  const [kpsReference] = useState(initialRef);
  const [grossAmount, setGrossAmount] = useState(initialAmount);
  const [customAmountInput, setCustomAmountInput] = useState(initialAmount > 0 ? String(initialAmount) : '');
  const [selectedNetwork, setSelectedNetwork] = useState(initialNetwork);
  const [senderPhone, setSenderPhone] = useState(initialPhone);

  // PENDING = Attente d'action client | PUSH_SENT = USSD ouvert, attente validation physique Agent | SETTLED = Payé & Validé
  const [paymentStatus, setPaymentStatus] = useState('PENDING'); 
  const [settledAt, setSettledAt] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasAutoTriggeredRef = useRef(false);

  const vaultPhone = KPS_VAULT_NUMBERS[selectedNetwork] || '0749475280';
  const split = useMemo(() => calculateKpsSplit(grossAmount), [grossAmount]);

  const autoPushDialUri = useMemo(() => {
    if (selectedNetwork === 'ORANGE_CI') return `tel:${encodeURIComponent(`#144*1*1*${vaultPhone}*${split.gross}#`)}`;
    if (selectedNetwork === 'MTN_CI') return `tel:${encodeURIComponent(`*133*1*1*${vaultPhone}*${split.gross}#`)}`;
    return null;
  }, [selectedNetwork, vaultPhone, split.gross]);

  const sha256Seal = useMemo(() => {
    return computeSha256Sync(`KPS|WEB_AUTO|${kpsReference}|${split.gross}|${split.net}|${split.fee}|${vaultPhone}|${selectedNetwork}|${paymentStatus}`);
  }, [kpsReference, split.gross, split.net, split.fee, vaultPhone, selectedNetwork, paymentStatus]);

  // Boucle de vérification (polling) : écoute si l'Agent KPS a validé l'encaissement
  const checkIntentStatus = useCallback(async () => {
    if (!kpsReference) return;
    try {
      const { data: intentRow } = await supabase.from('kps_payment_intents').select('*').eq('kps_reference', kpsReference).maybeSingle();
      if (intentRow) {
        if (intentRow.gross_amount_fcfa && grossAmount === 0) {
          setGrossAmount(Number(intentRow.gross_amount_fcfa));
          setCustomAmountInput(String(intentRow.gross_amount_fcfa));
        }
        if (intentRow.status === 'SETTLED') {
          setPaymentStatus('SETTLED');
          setSettledAt(intentRow.settled_at || new Date().toISOString());
        }
      }
    } catch (_e) {}
  }, [kpsReference, grossAmount]);

  useEffect(() => {
    checkIntentStatus();
    const interval = setInterval(checkIntentStatus, 3000);
    return () => clearInterval(interval);
  }, [checkIntentStatus]);

  // Déclenchement automatique de l'USSD pour le client
  const triggerAutomaticClientDebit = useCallback(async (networkToUse = selectedNetwork) => {
    if (split.gross <= 0) return;
    setIsProcessing(true);
    
    // Le statut passe en "Attente de l'Agent" et non pas en "Settled"
    setPaymentStatus('PUSH_SENT');

    try {
      const targetVault = KPS_VAULT_NUMBERS[networkToUse] || '0749475280';
      if (navigator.clipboard && networkToUse === 'WAVE_CI') {
        await navigator.clipboard.writeText(targetVault).catch(() => {});
      }

      if (networkToUse === 'ORANGE_CI') {
        window.location.href = `tel:${encodeURIComponent(`#144*1*1*${targetVault}*${split.gross}#`)}`;
      } else if (networkToUse === 'MTN_CI') {
        window.location.href = `tel:${encodeURIComponent(`*133*1*1*${targetVault}*${split.gross}#`)}`;
      }

      // Création de l'intention DANS LE SERVEUR, mais SANS valider la balance (auto_settle = false)
      await supabase.functions.invoke('kps-pay-engine', {
        body: {
          action: 'CREATE_INTENT', // Ne touche pas à la balance SQL
          kps_reference: kpsReference,
          amount_fcfa: split.gross,
          phone_number: senderPhone || '0700000000',
          preferred_network: networkToUse,
          auto_settle: false, // ATTENTE DE LA VALIDATION PHYSIQUE
        },
      });
    } catch (_e) {
    } finally {
      setIsProcessing(false);
    }
  }, [selectedNetwork, split.gross, kpsReference, senderPhone]);

  useEffect(() => {
    if (shouldAutoLaunch && split.gross > 0 && !hasAutoTriggeredRef.current) {
      hasAutoTriggeredRef.current = true;
      const timer = setTimeout(() => {
        if (selectedNetwork === 'ORANGE_CI' || selectedNetwork === 'MTN_CI') {
          if (autoPushDialUri) window.location.href = autoPushDialUri;
        }
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoLaunch, split.gross, selectedNetwork, autoPushDialUri]);

  const handlePhoneInput = (val) => {
    setSenderPhone(val);
    const digits = val.replace(/\D/g, '').replace(/^(225|00225)/, '');
    if (digits.startsWith('05')) setSelectedNetwork('MTN_CI');
  };

  return (
    <div className="min-h-screen font-sans py-8 px-4 flex flex-col items-center justify-between" style={{ backgroundColor: '#0A0D14', color: '#FFFFFF', minHeight: '100vh', width: '100%', margin: 0, padding: '2rem 1rem' }}>
      <div className="w-full max-w-xl rounded-3xl p-6 md:p-8 shadow-2xl" style={{ backgroundColor: '#131722', color: '#FFFFFF', border: '1px solid rgba(212, 175, 55, 0.25)' }}>
        <div className="flex items-center justify-between pb-5 mb-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(212, 175, 55, 0.15)' }}>
              <span style={{ color: '#D4AF37' }} className="font-black text-lg tracking-wider">KPS</span>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span style={{ color: '#D4AF37' }} className="text-[10px] font-extrabold uppercase tracking-widest">KPS Auto-Push • Débit Direct</span>
              </div>
              <h1 className="text-white font-extrabold text-lg uppercase tracking-wider mt-0.5">KAPITAL STATES CÔTE D'IVOIRE</h1>
            </div>
          </div>
          <div className="text-right">
            <span className="text-white/50 text-[10px] font-bold uppercase block">Référence Acte</span>
            <span style={{ color: '#D4AF37' }} className="font-mono text-xs font-extrabold">{kpsReference.length > 18 ? `${kpsReference.slice(0, 15)}...` : kpsReference}</span>
          </div>
        </div>

        {paymentStatus === 'SETTLED' ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4 text-3xl font-black">✓</div>
            <div className="inline-block bg-emerald-500/20 text-emerald-400 text-[10px] font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">Paiement Validé par l'Agent KPS</div>
            <h2 className="text-2xl font-black uppercase tracking-wider text-white mb-2">Certificat de Dépôt Séquestre</h2>
            <p className="text-white/60 text-xs mb-6">Les fonds ont été confirmés et verrouillés dans le coffre KPS-VAULT-ESCROW.</p>

            <div className="rounded-2xl p-5 mb-5 text-left space-y-3" style={{ backgroundColor: '#090C12' }}>
              <div className="flex justify-between items-center"><span className="text-white/50 text-xs font-bold uppercase">Référence Acte</span><span style={{ color: '#D4AF37' }} className="font-mono text-xs font-extrabold">{kpsReference}</span></div>
              <div className="flex justify-between items-center"><span className="text-white/50 text-xs font-bold uppercase">Montant Validé</span><span className="text-white text-sm font-extrabold">{split.gross.toLocaleString('fr-FR')} FCFA</span></div>
              <div className="h-[1px] bg-white/10 my-1" />
              <div className="flex justify-between items-center"><span className="text-emerald-400 text-xs font-extrabold uppercase">Crédité sur KPS-VAULT-ESCROW</span><span className="text-emerald-400 text-lg font-black">+{split.net.toLocaleString('fr-FR')} FCFA</span></div>
              <div className="flex justify-between items-center pt-1"><span className="text-white/50 text-xs font-bold uppercase">Date du Sceau</span><span className="text-white/80 text-xs font-mono">{new Date(settledAt || Date.now()).toLocaleString('fr-FR')}</span></div>
            </div>

            <div className="rounded-2xl p-4 mb-6 text-center" style={{ backgroundColor: '#090C12' }}>
              <div style={{ color: '#D4AF37' }} className="text-[10px] font-extrabold uppercase tracking-widest mb-1">Sceau Cryptographique SHA-256 (FIPS 180-4)</div>
              <div className="text-emerald-400 font-mono text-[10px] break-all font-bold">{sha256Seal}</div>
            </div>
            <button onClick={() => window.print()} style={{ backgroundColor: '#D4AF37', color: '#0A0D14' }} className="w-full font-extrabold text-xs uppercase tracking-widest py-4 rounded-2xl shadow-lg">Télécharger mon Certificat PDF Scellé</button>
          </div>
        ) : paymentStatus === 'PUSH_SENT' ? (
          <div className="text-center py-10">
            <div className="w-16 h-16 border-4 border-t-[#D4AF37] border-white/10 rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-xl font-black uppercase tracking-wider text-white mb-2">Finalisez sur votre téléphone</h2>
            <p className="text-white/70 text-sm mb-6 max-w-sm mx-auto">
              1. Tapez votre code secret {selectedNetwork.replace('_CI', '')} sur l'écran de votre téléphone.<br/><br/>
              2. Dès que votre transfert est terminé, le Terminal KPS le détectera automatiquement et cette page affichera votre reçu.
            </p>
            <div className="bg-[#090C12] p-4 rounded-xl border border-white/5">
              <span className="text-[#D4AF37] font-bold text-xs uppercase animate-pulse">Recherche du paiement en cours...</span>
            </div>
          </div>
        ) : (
          <div>
            <div className="rounded-2xl p-5 mb-5 text-center" style={{ backgroundColor: '#090C12' }}>
              <span className="text-white/50 text-[10px] font-extrabold uppercase tracking-widest block mb-1">Montant Net à Débiter</span>
              {initialAmount > 0 ? (
                <div style={{ color: '#D4AF37' }} className="text-4xl font-black tracking-tight my-1">{split.gross.toLocaleString('fr-FR')} <span className="text-lg">FCFA</span></div>
              ) : (
                <div className="my-2"><input type="number" placeholder="Montant en FCFA" value={customAmountInput} onChange={(e) => { setCustomAmountInput(e.target.value); setGrossAmount(parseInt(e.target.value || '0', 10) || 0); }} style={{ backgroundColor: '#131722', color: '#D4AF37' }} className="w-full text-2xl font-black text-center py-3 px-4 rounded-xl outline-none" /></div>
              )}
            </div>

            <div className="rounded-2xl p-5 mb-5" style={{ backgroundColor: '#090C12' }}>
              <label className="text-white/60 text-[10px] font-extrabold uppercase tracking-widest block mb-2">1. Votre Numéro à Débiter</label>
              <input type="tel" placeholder="Ex: 07 08 09 10 11" value={senderPhone} onChange={(e) => handlePhoneInput(e.target.value)} style={{ backgroundColor: '#131722', color: '#FFFFFF' }} className="w-full text-lg font-extrabold text-center py-3.5 px-4 rounded-xl outline-none mb-4" />

              <label className="text-white/60 text-[10px] font-extrabold uppercase tracking-widest block mb-2 text-center">2. Votre Opérateur</label>
              <div className="grid grid-cols-3 gap-2.5 p-1.5 rounded-2xl mb-5" style={{ backgroundColor: '#131722' }}>
                {[{ code: 'WAVE_CI', name: 'WAVE CI', color: '#1C88FF' }, { code: 'ORANGE_CI', name: 'ORANGE MONEY', color: '#FF6600' }, { code: 'MTN_CI', name: 'MTN MOMO', color: '#FFCC00' }].map((op) => {
                  const isSelected = selectedNetwork === op.code;
                  return (
                    <button key={op.code} type="button" onClick={() => setSelectedNetwork(op.code)} style={{ backgroundColor: isSelected ? op.color : 'transparent', color: isSelected ? '#0A0D14' : 'rgba(255,255,255,0.7)' }} className="py-3 px-2 rounded-xl font-black text-[11px] uppercase tracking-wider transition">{op.name}</button>
                  );
                })}
              </div>

              <button type="button" disabled={split.gross <= 0 || isProcessing} onClick={() => triggerAutomaticClientDebit(selectedNetwork)} style={{ backgroundColor: '#059669', color: '#FFFFFF' }} className="w-full disabled:opacity-40 font-black text-xs uppercase tracking-widest py-4 px-5 rounded-2xl shadow-lg">
                {isProcessing ? 'Génération en cours...' : `Déclencher ${split.gross.toLocaleString('fr-FR')} FCFA (${selectedNetwork.replace('_CI', '')})`}
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="text-center mt-6 text-white/40 text-[11px] space-y-1">
        <div style={{ color: '#D4AF37' }} className="font-extrabold text-[10px] uppercase tracking-widest mt-1">Propulsé par Kapital States © {new Date().getFullYear()}</div>
      </div>
    </div>
  );
}